<template>
  <!-- Page header: trial line, headline and progress ring.
  Sits directly on the page background — no card. -->
  <section class="pt-2 px-1 flex flex-col sm:flex-row items-start sm:items-center gap-6">
    <div class="flex-grow min-w-0">
      <BaseText
        v-if="trialLine"
        type="body"
        size="xs"
        class="text-text-subdued mb-2"
      >
        {{ trialLine }}
      </BaseText>
      <div
        v-if="copy.kicker"
        class="flex items-center gap-1.5 mb-1.5 text-text-subdued"
      >
        <RocketIcon
          :width="16"
          :height="16"
        />
        <BaseText
          type="heading"
          size="overline"
          class="uppercase"
        >
          {{ copy.kicker }}
        </BaseText>
      </div>
      <BaseText
        type="display"
        size="2xs"
        class="text-text-loud"
      >
        {{ copy.headline }}
      </BaseText>
      <BaseText
        type="body"
        size="sm"
        class="text-text-normal mt-2 max-w-2xl"
      >
        {{ copy.subhead }}
      </BaseText>
    </div>

    <!-- Progress ring: track + arc share one SVG so they always align -->
    <div class="flex-shrink-0 flex items-center gap-4">
      <svg
        class="progress-ring"
        width="84"
        height="84"
      >
        <circle
          class="text-neutral-50"
          stroke="currentColor"
          :stroke-width="6"
          fill="transparent"
          r="36"
          cx="42"
          cy="42"
        />
        <circle
          ref="progressCircle"
          class="progress-ring--arc text-primary-blue-100"
          stroke="currentColor"
          :stroke-width="6"
          stroke-linecap="round"
          fill="transparent"
          r="36"
          cx="42"
          cy="42"
          :stroke-dasharray="ringCircumference"
          :stroke-dashoffset="ringCircumference"
        />
      </svg>
      <div>
        <BaseText
          v-if="copy.progressLabel"
          type="body"
          size="xs"
          class="text-text-subdued mb-0.5"
        >
          {{ copy.progressLabel }}
        </BaseText>
        <BaseText
          type="display"
          size="2xs"
          class="text-text-loud"
        >
          {{ displayPercent }}%
        </BaseText>
        <BaseText
          type="body"
          size="xs"
          class="text-text-subdued mt-0.5"
        >
          {{ progress.stepsDone }} of {{ progress.stepsTotal }} steps
        </BaseText>
      </div>
    </div>
  </section>
</template>

<script>
import { animate, easeOut } from 'popmotion'
import RocketIcon from '../globals/Icons/RocketIcon.vue'

export default {
  name: 'SetupHeader',
  components: {
    RocketIcon
  },
  props: {
    // Resolved header copy: { kicker, headline, subhead, progressLabel }
    copy: {
      type: Object,
      required: true
    },
    // { percent, stepsDone, stepsTotal }
    progress: {
      type: Object,
      required: true
    },
    trialLine: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      // Animated 0 → percent so progress "counts up" on load
      displayPercent: 0,
      percentTween: null
    }
  },
  computed: {
    ringCircumference () {
      return 2 * Math.PI * 36
    }
  },
  watch: {
    'progress.percent' (percent) {
      this.animatePercent(percent)
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.animatePercent(this.progress.percent)
    })
  },
  beforeDestroy () {
    if (this.percentTween) this.percentTween.stop()
  },
  methods: {
    // Tween the number and the ring together from wherever they are now
    animatePercent (target) {
      if (this.percentTween) this.percentTween.stop()
      this.percentTween = animate({
        from: this.displayPercent,
        to: target,
        duration: 900,
        ease: easeOut,
        onUpdate: (value) => {
          this.displayPercent = Math.round(value)
          if (this.$refs.progressCircle) {
            this.$refs.progressCircle.style.strokeDashoffset =
              this.ringCircumference * (1 - value / 100)
          }
        },
        onComplete: () => {
          this.percentTween = null
        }
      })
    }
  }
}
</script>

<style scoped>
.progress-ring {
  transform: rotate(-90deg);
}
</style>
